import type { ThinkingChainHop, ThinkingChainHopStatus } from "./ThinkingChain";

/**
 * groupThinkingHops — collapse the raw per-event hop list into the
 * user-facing "passaggi" the `<ThinkingChain>` renders.
 *
 * The BE emits one `reasoning_hop` per agent graph node: every LLM call
 * yields a filler hop ("Sto ragionando…", `kind: "filler"`) and every tool
 * call yields a step hop with the tool copy. A single SQL turn produces 20+
 * hops, half of them filler and most of the rest repeats of the same tool.
 * Rendering them 1:1 is the flat wall the Sept 2026 redesign removes.
 *
 * Rules (pure, order-preserving):
 *   1. A completed filler extends the previous step's window. The active
 *      model request gets its own row, so the current work remains visible.
 *   2. Consecutive hops with the same `detail` merge into one group with a
 *      `count` (rendered as ×N).
 *   3. `startedAt` = first hop's start, `endedAt` = last hop's end. The
 *      window is clamped so the duration is never negative.
 *   4. Group status = last hop's status (only the tail can be `active`).
 */
export interface ThinkingStepGroup {
  /** Key for React — the first hop id of the group. */
  id: string;
  specialist: string;
  action?: string;
  label: string;
  detail: string;
  status: ThinkingChainHopStatus;
  /** How many hops were merged into this row (≥ 1). */
  count: number;
  startedAt?: number;
  endedAt?: number;
}

/** Durations below this are timing noise, not information — hidden. */
export const MIN_VISIBLE_STEP_MS = 500;

function detailOf(hop: ThinkingChainHop): string {
  return hop.detail ?? "";
}

export function groupThinkingHops(hops: readonly ThinkingChainHop[]): ThinkingStepGroup[] {
  const groups: ThinkingStepGroup[] = [];

  for (const hop of hops) {
    const last = groups.at(-1);
    const isFiller = hop.kind === "filler";
    const sameAsLast =
      last !== undefined &&
      !isFiller &&
      last.specialist === hop.specialist &&
      last.detail === detailOf(hop) &&
      !hop.action?.endsWith("_start");

    // Keep the running model request visible. Once the next real step lands,
    // it becomes a completed filler and folds back into the previous step.
    if (last && ((isFiller && hop.status !== "active") || sameAsLast)) {
      // Fold into the previous row: extend the window, carry the status
      // forward (the newest hop decides whether the row is still active).
      last.status = hop.status;
      if (!isFiller) last.count += 1;
      last.endedAt = hop.status === "active" ? undefined : (hop.endedAt ?? last.endedAt);
      continue;
    }

    groups.push({
      id: hop.id,
      specialist: hop.specialist,
      action: hop.action,
      label: hop.label,
      detail: detailOf(hop),
      status: hop.status,
      count: 1,
      startedAt: hop.startedAt,
      endedAt: hop.endedAt,
    });
  }

  return groups;
}

/** Group duration in ms, or `null` when unknown / too small to matter. */
export function stepDurationMs(group: ThinkingStepGroup): number | null {
  if (group.startedAt === undefined || group.endedAt === undefined) return null;
  const ms = Math.max(0, group.endedAt - group.startedAt);
  return ms < MIN_VISIBLE_STEP_MS ? null : ms;
}
